import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useForm } from 'react-hook-form';

import Button, { ButtonSize, ButtonVariant } from 'components/Button';
import { useAppSelector, useAppDispatch } from 'hooks';
import ContactSupport from 'components/ContactSupport';
import { Input, PhoneInput } from 'components/Inputs';
import HideComponent from 'components/HideComponent';
import {
  DomainType,
  getOtpLoginUrl,
  isSmartHubDomain,
} from 'utils/domainService';
import { LoginFormValues } from 'entities/login';
import { USER_WITH_OID } from 'services/persons';
import { QuestionIcon } from 'components/icons';
import { getDomain } from 'theme/selectors';
import { getUrlParams } from 'utils/url';
import Popover from 'components/Popover';
import { loginRequest } from 'config';
import path from 'common/path';

import { emailValidation, phoneValidation } from '../utils';
import { getLoginData, getCurrentTab } from '../selectors';
import { verifyPerson, notifyVerifyPersonFailed } from '../thunks';
import {
  LOGIN_TAB_INDEX_EMAIL,
  LOGIN_TAB_INDEX_PHONE,
  setData,
} from '../loginSlice';

const LoginForm = () => {
  const { t } = useTranslation('login');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { instance } = useMsal();

  const urlParams: any = getUrlParams();

  const data = useAppSelector(getLoginData);
  const currentTab = useAppSelector(getCurrentTab);
  const domain: string = useAppSelector(getDomain);

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    defaultValues: {
      email: urlParams?.$email || data.email,
      phone: urlParams?.$phone || data.phone,
    },
  });

  const email = watch('email');

  const handleMsaLogin = () => {
    instance.loginRedirect(loginRequest);
  };

  const handleCreate = async () => {
    dispatch(
      setData({
        email,
      }),
    );
    navigate(path.enroll);
  };

  const handleFormSubmit = async (values: LoginFormValues) => {
    const loginData: LoginFormValues = {};

    if (currentTab === LOGIN_TAB_INDEX_EMAIL) {
      loginData.email = values.email;
      loginData.sendCode = true;
    }

    if (currentTab === LOGIN_TAB_INDEX_PHONE) {
      loginData.phone = values.phone;
      loginData.sendCode = true;
    }

    dispatch(setData(loginData));

    // a page on a smart-hub.com host can't keep the OTP session cookie (the API is on smart-enroll.com,
    // a different site), so find the route without sending a code and send email-code users to smart-enroll.com
    const onSmartHubHost = isSmartHubDomain();

    const result = await dispatch(
      verifyPerson(
        onSmartHubHost ? { ...loginData, sendCode: false } : loginData,
      ),
    );

    if (!verifyPerson.fulfilled.match(result)) {
      notifyVerifyPersonFailed(dispatch, result);
      return;
    }

    // ErrorFound: nothing to act on, so say so rather than leaving the form silent
    if (result.payload == null) {
      notifyVerifyPersonFailed(dispatch, null);
      return;
    }

    // anyone who isn't a Microsoft user is an email-code user (or a new email), whatever the account state
    if (onSmartHubHost && result.payload !== USER_WITH_OID) {
      window.location.assign(getOtpLoginUrl(loginData));
      return;
    }

    if (result.payload === USER_WITH_OID) {
      handleMsaLogin();
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="py-8 px-4 md:px-8">
        <HideComponent show={currentTab === LOGIN_TAB_INDEX_EMAIL}>
          <Input
            name="email"
            placeholder={t('login:email.placeholder')}
            label={
              errors?.email?.type === 'required'
                ? t('login:email.labelRequired')
                : t('login:email.label')
            }
            register={register}
            error={errors?.email}
            validation={emailValidation(t)}
            readOnly={!!urlParams?.$email}
          />
        </HideComponent>
        <HideComponent show={currentTab === LOGIN_TAB_INDEX_PHONE}>
          <PhoneInput
            label={t('login:phone.label')}
            name="phone"
            error={errors?.phone}
            control={control}
            validation={phoneValidation(t)}
          />
        </HideComponent>

        <Button
          loading={isSubmitting}
          type="submit"
          className="my-6 w-full"
          heightClass="h-11"
          size={ButtonSize.mediumLarge}
        >
          {t('common:signIn')}
        </Button>

        <HideComponent hide={domain === DomainType.invoicecloud}>
          <div className="flex justify-center items-center">
            <p className="text-secondary font-medium px-2.5">
              {t('createAccount')}
            </p>
            <Button
              onClick={handleCreate}
              variant={ButtonVariant.link}
              size={ButtonSize.medium}
              heightClass="h-6"
              paddingClass="px-2.5"
            >
              {t('login:enroll')}
            </Button>
          </div>
        </HideComponent>

        <div className="flex justify-center">
          <Popover
            button={
              <Button
                variant={ButtonVariant.linkSecondary}
                className="mt-6"
                size={ButtonSize.medium}
              >
                <QuestionIcon className="mr-2 text-gray-400" />
                {t('common:helpTranscard')}
              </Button>
            }
            content={<ContactSupport />}
            overlay={true}
          />
        </div>
      </div>
    </form>
  );
};

export default LoginForm;
